import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

// 六维评分权重：账户级持久化（此前只存在浏览器 localStorage，换设备/浏览器就丢失）。
// weights === null 表示未设置覆盖，调用方应回退到该项目分析时保存的阶段自适应权重
// （project.analysis.stage_weights），而不是某一套固定权重 —— 见 utils/scoring.js 的
// effectiveComposite()。localStorage 仍作为未登录用户的本地兜底，登录后以 Supabase 为准
// （因为可能是换设备登录，本地缓存不一定是最新的）。
const WEIGHTS_KEY = 'alpha_os_weights';

function loadLocal() {
  try { return JSON.parse(localStorage.getItem(WEIGHTS_KEY) || 'null'); } catch { return null; }
}
function saveLocal(w) {
  try {
    if (w) localStorage.setItem(WEIGHTS_KEY, JSON.stringify(w));
    else localStorage.removeItem(WEIGHTS_KEY);
  } catch {}
}

export function useScoringWeights(user) {
  const [weights, setWeightsState] = useState(loadLocal);
  const [loading, setLoading] = useState(false);

  // 登录后拉取账户端保存的权重，以远端为准覆盖本地缓存
  useEffect(() => {
    if (!user || !supabase) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('custom_weights')
        .eq('id', user.id)
        .maybeSingle();
      if (cancelled) return;
      if (error) {
        console.warn('[scoring-weights] 拉取账户自定义权重失败:', error.message);
      } else if (data && 'custom_weights' in data) {
        setWeightsState(data.custom_weights);
        saveLocal(data.custom_weights);
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [user]);

  const setWeights = useCallback((w) => {
    setWeightsState(w);
    saveLocal(w);
    if (user && supabase) {
      supabase.from('profiles').update({ custom_weights: w }).eq('id', user.id)
        .then(({ error }) => { if (error) console.warn('[scoring-weights] 保存到账户失败:', error.message); });
    }
  }, [user]);

  const clearWeights = useCallback(() => setWeights(null), [setWeights]);

  return { weights, setWeights, clearWeights, loading };
}
